import React, { useState, useEffect } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Card from '../Card';
import Table from '../Table';
import Modal from '../Modal';

const TariffsSection = ({ api, onRefresh }) => {
  const [tariffs, setTariffs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsTotalAmount, setPaymentsTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showPayments, setShowPayments] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const defaultForm = { name: '', daily_limit: 0, monthly_limit: 0, price_per_month: 0, type: 'individual', participant_limit: null, is_active: true, is_hidden: false, description: '', period_days: 30 };
  const [form, setForm] = useState(defaultForm);

  const filteredPayments = payments.filter(p => {
    const searchLower = paymentSearch.toLowerCase();
    const matchesSearch = !paymentSearch || 
      (p.user_name || '').toLowerCase().includes(searchLower) ||
      (p.user_email || '').toLowerCase().includes(searchLower) ||
      String(p.amount).includes(searchLower);
    const matchesStatus = !paymentStatusFilter || p.status === paymentStatusFilter;
    const paymentDate = p.created_at ? new Date(p.created_at) : null;
    const matchesDateFrom = !dateFrom || (paymentDate && paymentDate >= new Date(dateFrom));
    const matchesDateTo = !dateTo || (paymentDate && paymentDate <= new Date(dateTo + 'T23:59:59'));
    return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tariffsData, paymentsData] = await Promise.all([
        api.fetchTariffs(),
        api.fetchPayments()
      ]);
      setTariffs(tariffsData);
      setPayments(paymentsData.payments);
      setPaymentsTotal(paymentsData.total);
      setPaymentsTotalAmount(paymentsData.totalAmount);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTariff(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const handleEdit = (tariff) => {
    setEditingTariff(tariff);
    setForm({
      name: tariff.name,
      daily_limit: tariff.daily_limit,
      monthly_limit: tariff.monthly_limit,
      price_per_month: tariff.price_per_month,
      type: tariff.type || 'individual',
      participant_limit: tariff.participant_limit,
      is_active: tariff.is_active,
      is_hidden: tariff.is_hidden || false,
      description: tariff.description || '',
      period_days: tariff.period_days || 30
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      await api.saveTariff(editingTariff ? { ...form, id: editingTariff.id } : form, !!editingTariff);
      toast.success(editingTariff ? 'Тариф обновлён' : 'Тариф создан');
      setShowModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (tariffId) => {
    if (!window.confirm('Деактивировать тариф?')) return;
    try {
      await api.deleteTariff(tariffId);
      toast.success('Тариф деактивирован');
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const tariffColumns = [
    { header: 'Название', render: (row) => <span className="font-medium text-gray-900 dark:text-gray-100">{row.name}</span> },
    { header: 'Тип', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.type === 'b2b' ? 'B2B' : 'Индивидуальный'}</span> },
    { header: 'Лимит/день', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.daily_limit || '∞'}</span> },
    { header: 'Лимит/мес', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.monthly_limit || '∞'}</span> },
    { header: 'Цена', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.price_per_month ? `${row.price_per_month} ₽` : 'Бесплатно'}</span> },
    { header: 'Статус', render: (row) => <span className={`px-2 py-1 rounded text-xs ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{row.is_active ? 'Активен' : 'Неактивен'}</span> },
    { header: 'Действия', render: (row) => (
      <div className="flex gap-1">
        <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline text-xs">Ред.</button>
        <button onClick={() => handleDelete(row.id)} className="text-red-600 hover:underline text-xs">Удал.</button>
      </div>
    )}
  ];

  const paymentColumns = [
    { header: 'Пользователь', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.user_name || row.user_email || row.user_id}</span> },
    { header: 'Сумма', render: (row) => <span className="font-medium text-green-700 dark:text-green-400">{row.amount?.toLocaleString()} ₽</span> },
    { header: 'Статус', className: 'hidden sm:table-cell', render: (row) => (
      <span className={`px-2 py-1 rounded text-xs ${row.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : row.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
        {row.status === 'success' ? 'Успешно' : row.status === 'pending' ? 'Ожидание' : 'Ошибка'}
      </span>
    )},
    { header: 'Дата', className: 'hidden sm:table-cell', render: (row) => <span className="text-gray-600 dark:text-gray-400">{row.created_at ? new Date(row.created_at).toLocaleString('ru') : '—'}</span> }
  ];

  return (
    <>
      {/* Tariffs Card */}
      <Card title="💰 Тарифы" headerActions={
        <button onClick={handleAdd} className="bg-green-500 text-white rounded px-3 py-1 hover:bg-green-600 text-sm">Добавить тариф</button>
      }>
        <Table columns={tariffColumns} data={tariffs} loading={loading} emptyMessage="Нет тарифов" />
      </Card>

      {/* Payments Card */}
      <Card title="Платежи" icon={ChartBarIcon} headerActions={
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">Всего: <span className="font-bold text-green-600">{paymentsTotalAmount.toLocaleString()} ₽</span></span>
          <button onClick={() => setShowPayments(!showPayments)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm font-medium">
            {showPayments ? 'Скрыть историю' : 'Показать историю'}
          </button>
        </div>
      }>
        {showPayments && (
          <>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input type="text" value={paymentSearch} onChange={e => setPaymentSearch(e.target.value)} placeholder="Поиск по имени, email, сумме..." className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 text-sm" />
              <select value={paymentStatusFilter} onChange={e => setPaymentStatusFilter(e.target.value)} className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 text-sm">
                <option value="">Все статусы</option>
                <option value="success">Успешно</option>
                <option value="pending">Ожидание</option>
                <option value="failed">Ошибка</option>
              </select>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 text-sm" title="С даты" />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 text-sm" title="По дату" />
            </div>
            <div className="overflow-y-auto max-h-64">
              <Table columns={paymentColumns} data={filteredPayments} loading={loading} emptyMessage="Нет платежей" />
            </div>
            {paymentsTotal > 0 && <div className="text-right text-xs text-gray-500 mt-2">Показано {filteredPayments.length} из {paymentsTotal}</div>}
          </>
        )}
      </Card>

      {/* Tariff Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingTariff ? 'Редактировать тариф' : 'Новый тариф'} size="md">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Название</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" placeholder="Введите название" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Тип тарифа</label>
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100">
              <option value="individual">B2C (Индивидуальный)</option>
              <option value="b2b">B2B (Организации)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Лимит токенов/день</label>
              <input type="number" min="0" value={form.daily_limit || ''} onChange={e => setForm(p => ({ ...p, daily_limit: e.target.value === '' ? 0 : Math.max(0, +e.target.value) }))} className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white" placeholder="0 = безлимит" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Лимит токенов/период</label>
              <input type="number" min="0" value={form.monthly_limit || ''} onChange={e => setForm(p => ({ ...p, monthly_limit: e.target.value === '' ? 0 : Math.max(0, +e.target.value) }))} className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white" placeholder="0 = безлимит" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Цена ₽/период</label>
              <input type="number" min="0" value={form.price_per_month || ''} onChange={e => setForm(p => ({ ...p, price_per_month: e.target.value === '' ? 0 : Math.max(0, +e.target.value) }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" placeholder="0 = бесплатно" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Период (дней)</label>
              <input type="number" min="1" value={form.period_days || ''} onChange={e => setForm(p => ({ ...p, period_days: e.target.value === '' ? 30 : Math.max(1, +e.target.value) }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" placeholder="30" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание</label>
            <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100 resize-none" rows={2} placeholder="Описание тарифа" />
          </div>
          {form.type === 'b2b' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Лимит участников</label>
              <input type="number" min="1" value={form.participant_limit || ''} onChange={e => setForm(p => ({ ...p, participant_limit: e.target.value === '' ? null : Math.max(1, +e.target.value) }))} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100" placeholder="Макс. участников" />
            </div>
          )}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 dark:text-white">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
              Активен
            </label>
            <label className="flex items-center gap-2 dark:text-white">
              <input type="checkbox" checked={form.is_hidden || false} onChange={e => setForm(p => ({ ...p, is_hidden: e.target.checked }))} />
              Скрытый
            </label>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSave} className="flex-1 bg-blue-600 text-white rounded py-2 hover:bg-blue-700">Сохранить</button>
          <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded">Отмена</button>
        </div>
      </Modal>
    </>
  );
};

export default TariffsSection;
